/**
 * Shared markdown renderer — converts markdown text to styled HTML.
 * Used by chat-panel.ts (messages) and deliverables-panel.ts (viewer).
 *
 * Supports: ## headers, **bold**, *italic*, - bullets, 1. numbered lists,
 * --- rules, ```code blocks```, and line breaks.
 *
 * Security: always escapes HTML before processing markdown patterns.
 */

/** Escape HTML entities to prevent XSS */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Convert markdown text to styled HTML.
 * Escapes HTML first, then applies markdown formatting.
 */
export function formatMarkdown(text: string): string {
  let html = escapeHtml(text);

  // Code blocks (triple backtick) — must be before line break processing
  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, '<pre class="md-code"><code>$2</code></pre>');

  // Line breaks (before block-level processing)
  html = html.replace(/\n/g, '<br>');

  // Headers: ### before ## (more specific first)
  html = html.replace(/(^|<br>)\s*###\s+(.+?)(<br>|$)/g, '$1<h4 class="md-h4">$2</h4>$3');
  html = html.replace(/(^|<br>)\s*##\s+(.+?)(<br>|$)/g, '$1<h3 class="md-h3">$2</h3>$3');
  html = html.replace(/(^|<br>)\s*#\s+(.+?)(<br>|$)/g, '$1<h2 class="md-h2">$2</h2>$3');

  // Horizontal rules
  html = html.replace(/(^|<br>)\s*---\s*(<br>|$)/g, '$1<hr class="md-hr">$2');

  // Bold: **text** and __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic: *text* (but not inside bold **)
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

  // Inline code: `code`
  html = html.replace(/`([^`]+)`/g, '<code class="md-inline-code">$1</code>');

  // Links: [text](url) — only allow http/https, add rel safety attrs
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Bullet lists: - item or • item
  html = html.replace(/(^|<br>)\s*[-•]\s+/g, '$1<span class="md-bullet">•</span> ');

  // Numbered lists: 1. item or 1) item
  html = html.replace(/(^|<br>)\s*(\d+)[.)]\s+/g, '$1<span class="md-number">$2.</span> ');

  return html;
}
